/**
 * Action runtime for page buttons (ADR-0010 D12 · P7b3 · S12; ADR-0022 conditional
 * chains).
 *
 * A button's `on_click` is an array of actions run in order. Families:
 *  - CLIENT: `set_variable` / `navigate` / `open_drawer` / `refresh` / `toast`.
 *  - OVERLAY (ADR-0023): `open_overlay` (open an authored page as a modal/drawer,
 *    seeding its read-only `$input` from a `context` map, with an optional
 *    `on_close` sub-chain run in the opener scope with `$result` bound) and
 *    `close_overlay` (close the current overlay, returning a `result` map).
 *  - SERVER: `create_row` / `update_row` / `delete_row` — write to a Data Table
 *    through the portal API (role write-policy gated server-side).
 *  - INTEGRATION: `run_workflow` / `run_query` (a manual query OR a write op).
 *  - CONTROL (ADR-0022): `if` (condition → `then` / `else` sub-chain) and
 *    `try` (run `do`; on a failure run `catch`, with `$page.error` set, then the
 *    OUTER chain continues — so a failure can be handled instead of hard-stopping).
 *
 * CONTINUATIONS (ADR-0043 F4): every LEAF action may carry an `on_success` and/or
 * an `on_failure` sub-chain — the per-action verb/SDK continuation model. After a
 * leaf runs, `on_success` runs on completion; on a failure `on_failure` runs with
 * `{{ $page.error }}` bound (like a `try`'s `catch` but scoped to the one action),
 * and because the failure is handled locally the OUTER chain then continues. A
 * leaf that fails with NO `on_failure` keeps the legacy behaviour (stop + toast,
 * unless wrapped in a `try`). Control-flow actions (`if`/`try`) own their own
 * branching and take no continuations.
 *
 * Every string argument is a `{{ }}` template evaluated against the current page
 * scope. A server/integration failure stops the chain (later actions don't run on
 * a half-applied mutation) — UNLESS it's caught by a `try` or handled by an
 * `on_failure`. Unknown action types are ignored so older/newer ASTs degrade
 * gracefully.
 */
import { isJsObjectNamespace, type Scope, renderTemplate } from "./pageExpr";

export interface PageAction {
  type: string;
  [key: string]: unknown;
}

export interface ActionContext {
  scope: Scope;
  setVar: (name: string, value: unknown) => void;
  refresh: () => void;
  navigate: (to: string) => void;
  toast: (message: string) => void;
  /** Server row writes — provided by the portal runtime (bound to the app
   *  slug). Each rejects on a server error (4xx/5xx). */
  createRow: (tableId: string, data: Record<string, unknown>) => Promise<unknown>;
  updateRow: (tableId: string, rowId: string, data: Record<string, unknown>) => Promise<unknown>;
  deleteRow: (tableId: string, rowId: string) => Promise<unknown>;
  /** Ask the user to confirm before a guarded action (returns false = cancel,
   *  which stops the chain). Wraps `window.confirm` in the portal. */
  confirm: (message: string) => boolean;
  /** Run an opted-in app workflow (button_clicked trigger) with a payload. */
  runWorkflow: (workflowId: string, payload: Record<string, unknown>) => Promise<unknown>;
  /** Imperatively (re)run a named page query — the `.run()` of ADR-0043 F3
   *  (`run_query` action). Makes a `trigger: "manual"` query reachable; a
   *  failure REJECTS so the action chain's `try/catch` can react (it subsumes
   *  the former `run_operation` action — ADR-0044 J3c). */
  runQuery: (queryName: string) => Promise<void>;
  /** Render + download a granted report as PDF (ADR-0060 S5). The portal
   *  runtime binds it to the member-gated render route (the server re-checks
   *  the role grant); optional — a surface without a report seam no-ops. */
  printReport?: (reportId: string, params: Record<string, unknown>) => Promise<void>;
  /** Open another page in a slide-over drawer (e.g. a create form). */
  openDrawer: (pageSlug: string, vars?: Record<string, unknown>) => void;
  /** Open an authored overlay page (modal/drawer) on top of the current page
   *  (ADR-0023), seeding its read-only `$input` namespace. `onClose` runs the
   *  opener's `on_close` chain (with `$result` bound) once the overlay closes. */
  openOverlay: (opts: {
    page: string;
    mode: "modal" | "drawer";
    input: Record<string, unknown>;
    onClose?: (result: Record<string, unknown>) => void;
  }) => void;
  /** Close the overlay this chain runs inside, returning `result` to the
   *  opener's `on_close` (undefined when not inside an overlay). */
  closeOverlay?: (result: Record<string, unknown>) => void;
  /** Write a key into the app-scoped global `$global` (ADR-0043) — survives
   *  navigation, readable as `{{ $global.<key> }}`. */
  storeValue: (key: string, value: unknown) => void;
  /** Delete a key from the global `$global`. */
  removeValue: (key: string) => void;
  /** Clear the whole app `$global`. */
  clearStore: () => void;
  /** Open a `modal`/`drawer` COMPONENT on the current page by its node id
   *  (ADR-0045). Unlike `openOverlay` (which opens another authored PAGE), this
   *  toggles an in-page overlay container's runtime `open` state. */
  openModal: (target: string) => void;
  /** Close a `modal`/`drawer` component by node id (ADR-0045). */
  closeModal: (target: string) => void;
  /** Where actions run (ADR-0045 R3). `"preview"` = the BUILDER's running
   *  canvas: SANDBOX (navigation) + GATED (server write / query) actions are
   *  SUPPRESSED so testing the UI never leaves the builder nor mutates real
   *  data. Omitted / `"live"` = the portal runtime — everything runs. */
  mode?: ActionExecMode;
}

export type ActionExecMode = "live" | "preview";

/**
 * Where a leaf action may run in the BUILDER's running canvas (ADR-0045 R3):
 *  - `"live"` — pure client UI/state (modals, `$page`/`$global` state, toast)
 *    + the JS handlers (`run_js`/`run_js_inline`, whose own `tray.*` verbs are
 *    gated by the sandbox runtime) → ALWAYS safe to run.
 *  - `"sandbox"` — would leave the builder (navigation / open another page).
 *  - `"gated"` — touches the SERVER (data writes, workflow, query, refetch).
 * In `mode: "preview"` only `live` runs; `sandbox`/`gated` are suppressed with a
 * toast. In `live` mode the policy is not consulted. Control-flow (`if`/`try`)
 * isn't classified — it recurses and its leaves are judged individually.
 */
export function actionExecPolicy(type: string): "live" | "sandbox" | "gated" {
  switch (type) {
    case "open_modal":
    case "close_modal":
    case "set_variable":
    case "toast":
    case "global_value":
    case "global_remove":
    case "global_clear":
    case "run_js":
    case "run_js_inline":
      return "live";
    case "navigate":
    case "open_drawer":
    case "open_overlay":
    case "close_overlay":
      return "sandbox";
    default:
      // create_row / update_row / delete_row / run_workflow / run_query / refresh
      return "gated";
  }
}

/** Leaf action types the runtime understands (client + server + integration). */
export const ACTION_TYPES = [
  "set_variable",
  "navigate",
  "open_drawer",
  "open_overlay",
  "close_overlay",
  // ADR-0045: open/close an in-page modal/drawer COMPONENT by node id (distinct
  // from `open_overlay`, which opens another authored page).
  "open_modal",
  "close_modal",
  "refresh",
  "toast",
  "create_row",
  "update_row",
  "delete_row",
  "run_workflow",
  // `run_query` runs a manual query OR a (write) operation declared as a
  // `trigger:"manual"` operation-query (ADR-0044 J3c — it absorbed the former
  // `run_operation` action).
  "run_query",
  // ADR-0060 S5 — render + download a granted report as PDF (role-gated
  // server-side: the app link ∩ the role's report_type_ids allow-list).
  "print_report",
  "run_js",
  // ADR-0044 U-track: the WHOLE handler as inline JS (the "edit as code" toggle).
  // `code` runs against the verb SDK below — the action-chain twin of `run_js`'s
  // JS-Object call. Stored when the operator flips a handler to JS mode.
  "run_js_inline",
  "global_value",
  "global_remove",
  "global_clear",
] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

/** Control-flow action types (ADR-0022): branch + error handling. They own
 *  nested action sub-chains rather than leaf args. */
export const CONTROL_ACTION_TYPES = ["if", "try"] as const;
export type ControlActionType = (typeof CONTROL_ACTION_TYPES)[number];

/** Server actions write to a Data Table; they share `table` + (for
 *  update/delete) `row`, and (for create/update) a `values` map. */
export const SERVER_ACTION_TYPES: readonly ActionType[] = [
  "create_row",
  "update_row",
  "delete_row",
];

/**
 * Deep-scan an arbitrary value (typically a node's `props`) for the `$global`
 * keys a page WRITES via `global_value` / `global_remove` actions — wherever the
 * action chain sits (`on_click`, an overlay's `on_close`, a nested `if`/`try`
 * sub-chain, a row action). Used by the builder to seed `$global` intellisense
 * (ADR-0043) so `{{ $global.<key> }}` autocompletes the keys actually in use.
 * Pure + total; a non-object/array is a no-op.
 */
export function collectGlobalKeys(value: unknown, out: Set<string> = new Set()): Set<string> {
  if (Array.isArray(value)) {
    for (const v of value) collectGlobalKeys(v, out);
  } else if (value && typeof value === "object") {
    const o = value as Record<string, unknown>;
    if ((o.type === "global_value" || o.type === "global_remove") && typeof o.key === "string") {
      const k = o.key.trim();
      if (k) out.add(k);
    }
    for (const v of Object.values(o)) collectGlobalKeys(v, out);
  }
  return out;
}

/** Resolve an argument: render `{{ }}` templates, else pass through. */
function arg(value: unknown, scope: Scope): unknown {
  if (typeof value === "string" && value.includes("{{")) {
    try {
      return renderTemplate(value, scope);
    } catch {
      return "";
    }
  }
  return value;
}

/** Coerce a rendered condition to a boolean. A single `{{ expr }}` yields the
 *  typed value (often a real boolean); a stray string "false"/"0"/"" is falsy. */
function truthy(v: unknown): boolean {
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s !== "" && s !== "false" && s !== "0" && s !== "null" && s !== "undefined";
  }
  return Boolean(v);
}

/** Render every value of a `{key: template}` map against the scope. Exported so
 *  the `page_ref` component (ADR-0045 R4) can evaluate its `params` map against
 *  the HOST page scope before seeding the embedded page's `$input`. */
export function renderValues(values: unknown, scope: Scope): Record<string, unknown> {
  if (!values || typeof values !== "object") return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values as Record<string, unknown>)) {
    out[k] = arg(v, scope);
  }
  return out;
}

function errorMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) return String((e as { message: unknown }).message);
  return "Action failed";
}

/**
 * The `tray` SDK handed to a JS Object async handler (ADR-0044 J2) — the unified
 * action-verb set as callables. A handler does its CLIENT logic and reaches data
 * only through these verbs (which re-enforce RLS server-side); it never touches
 * the DB directly. A curated view of the `ActionContext`, so a handler can never
 * do more than a button's action chain could.
 */
function buildTray(ctx: ActionContext, setVar: (name: string, value: unknown) => void) {
  const sdk = {
    navigate: (to: unknown) => ctx.navigate(String(to)),
    toast: (m: unknown) => ctx.toast(String(m)),
    refresh: () => ctx.refresh(),
    confirm: (m: unknown) => ctx.confirm(String(m)),
    setVar: (name: unknown, value: unknown) => setVar(String(name), value),
    runQuery: (name: unknown) => ctx.runQuery(String(name)),
    printReport: (report: unknown, params?: Record<string, unknown>) =>
      ctx.printReport?.(String(report), params ?? {}) ?? Promise.resolve(),
    runWorkflow: (id: unknown, payload?: Record<string, unknown>) =>
      ctx.runWorkflow(String(id), payload ?? {}),
    createRow: (table: unknown, data?: Record<string, unknown>) =>
      ctx.createRow(String(table), data ?? {}),
    updateRow: (table: unknown, row: unknown, data?: Record<string, unknown>) =>
      ctx.updateRow(String(table), String(row), data ?? {}),
    deleteRow: (table: unknown, row: unknown) => ctx.deleteRow(String(table), String(row)),
    openDrawer: (page: unknown, vars?: Record<string, unknown>) =>
      ctx.openDrawer(String(page), vars ?? {}),
    openModal: (target: unknown) => ctx.openModal(String(target)),
    closeModal: (target: unknown) => ctx.closeModal(String(target)),
    global: {
      set: (key: unknown, value: unknown) => ctx.storeValue(String(key), value),
      remove: (key: unknown) => ctx.removeValue(String(key)),
      clear: () => ctx.clearStore(),
    },
  };
  // ADR-0045 R3 — in the builder preview the JS handler's DATA/NAV verbs are
  // suppressed too (the policy in `runOne` only gates STRUCTURED leaves; a
  // `run_js`/`run_js_inline` handler calls these verbs directly). The UI/state
  // verbs (toast/setVar/openModal/closeModal/global) pass through.
  if (ctx.mode === "preview") {
    const dataBlock = () => void ctx.toast("Azione dati disabilitata in anteprima");
    const navBlock = () => void ctx.toast("Navigazione disabilitata in anteprima");
    Object.assign(sdk, {
      navigate: () => navBlock(),
      openDrawer: () => navBlock(),
      refresh: () => dataBlock(),
      runQuery: async () => void dataBlock(),
      printReport: async () => void dataBlock(),
      runWorkflow: async () => (dataBlock(), undefined),
      createRow: async () => (dataBlock(), undefined),
      updateRow: async () => (dataBlock(), undefined),
      deleteRow: async () => (dataBlock(), undefined),
    });
  }
  return sdk;
}

/**
 * The verb SDK for the `run_js_inline` action (ADR-0044 U-track) — a SUPERSET of
 * `buildTray` that also exposes the action-chain vocabulary as bare callables
 * (`openOverlay`/`closeOverlay`/`storeValue`/`removeValue`/`clearStore`), so the
 * JS serialized from a structured chain (`lib/actionsJs`) runs verbatim. Same
 * trust + data boundary as `buildTray`: reaches data ONLY through these verbs
 * (RLS re-forced server-side), never the DB directly.
 */
function buildInlineJsSdk(ctx: ActionContext, setVar: (name: string, value: unknown) => void) {
  const preview = ctx.mode === "preview";
  const navBlock = () => void ctx.toast("Navigazione disabilitata in anteprima");
  return {
    ...buildTray(ctx, setVar), // already preview-gates the data/nav verbs
    // openOverlay/closeOverlay open another authored PAGE → SANDBOX in preview.
    openOverlay: preview
      ? () => navBlock()
      : (page: unknown, mode?: unknown) =>
          ctx.openOverlay({ page: String(page), mode: mode === "modal" ? "modal" : "drawer", input: {} }),
    closeOverlay: preview
      ? () => navBlock()
      : (result?: unknown) =>
          ctx.closeOverlay?.(result && typeof result === "object" ? (result as Record<string, unknown>) : {}),
    storeValue: (key: unknown, value: unknown) => ctx.storeValue(String(key), value),
    removeValue: (key: unknown) => ctx.removeValue(String(key)),
    clearStore: () => ctx.clearStore(),
  };
}

/**
 * Compile + run an inline-JS handler (`run_js_inline.code`) against the verb SDK
 * + the page bindings. Operator-authored page config (page-config trust), run in
 * the user's own browser over their own authorized data — the ADR-0044 J1 "client
 * native-JS host". `new Function` (not the restricted `pageExpr` interpreter)
 * because this IS real JS, exactly like a JS Object body; the data boundary is
 * the verb SDK, never a DB reach. Wrapped in an async IIFE so the code may
 * `await`. Throws on a code error so the caller routes it to `on_failure`.
 */
const ID_RE = /^[A-Za-z_$][\w$]*$/;

async function runInlineJs(
  code: string,
  ctx: ActionContext,
  setVar: (name: string, value: unknown) => void,
  scope: Scope,
): Promise<void> {
  const sdk = buildInlineJsSdk(ctx, setVar);
  const s = scope as Record<string, unknown>;

  // The whole binding ENVIRONMENT in scope: every verb (`navigate`/`toast`/…),
  // every page binding ($page/$queries/$global/…) AND every JS Object namespace
  // (`Deals`/`Utils`) the scope already carries, so the operator's code can call
  // them by name — matching what the editor's IntelliSense advertises.
  const env: Record<string, unknown> = { ...s, ...sdk };

  // Appsmith-style `<Query>.run()` — expose each declared query as a callable
  // object next to its data, so `Create_Post.run()` works (it maps to the SAME
  // governed `runQuery`). Only valid-identifier names get a top-level binding;
  // anything else stays reachable via `runQuery("name")`.
  const queries = (s.$queries && typeof s.$queries === "object" ? s.$queries : {}) as Record<string, unknown>;
  for (const name of Object.keys(queries)) {
    if (!ID_RE.test(name) || name in env) continue;
    const q = (queries[name] ?? {}) as Record<string, unknown>;
    env[name] = { run: () => ctx.runQuery(name), data: q.data, rows: q.rows };
  }

  const names = Object.keys(env).filter((k) => ID_RE.test(k));
  const fn = new Function(...names, `"use strict"; return (async () => { ${code}\n })();`);
  await fn(...names.map((k) => env[k]));
}

/** Shared mutable state across a single chain run. `localPage` overlays
 *  `$page` so a var set EARLIER in the chain is visible to LATER templates
 *  within the same run (React `setVar` is async). `lastError` carries the most
 *  recent failure so a `try`'s `catch` can read `{{ $page.error }}`. */
interface RunState {
  localPage: Record<string, unknown>;
  lastError: string | null;
}

/**
 * Run a `button.on_click` action chain. Non-array / malformed input is a no-op;
 * unknown action types are skipped. Async because server actions hit the
 * network — the chain awaits each in order; a failed write stops the chain
 * (unless caught by an enclosing `try`).
 */
export async function runActions(actions: unknown, ctx: ActionContext): Promise<void> {
  await runChain(actions, ctx, { localPage: {}, lastError: null }, false);
}

/** Run a (sub-)chain. Returns `false` if it was stopped (failure / cancel),
 *  `true` if it completed. `inTry` suppresses the default error toast so an
 *  enclosing `try`'s `catch` owns the messaging. */
async function runChain(
  actions: unknown,
  ctx: ActionContext,
  state: RunState,
  inTry: boolean,
): Promise<boolean> {
  if (!Array.isArray(actions)) return true;
  for (const a of actions) {
    if (!a || typeof a !== "object") continue;
    const ok = await runOne(a as PageAction, ctx, state, inTry);
    if (!ok) return false;
  }
  return true;
}

async function runOne(
  action: PageAction,
  ctx: ActionContext,
  state: RunState,
  inTry: boolean,
): Promise<boolean> {
  const scopeNow = (): Scope => {
    if (!Object.keys(state.localPage).length) return ctx.scope;
    const base = ctx.scope as Record<string, unknown>;
    const page = base.$page && typeof base.$page === "object" ? (base.$page as Record<string, unknown>) : {};
    return { ...base, $page: { ...page, ...state.localPage } } as Scope;
  };
  const assign = (name: string, value: unknown) => {
    if (!name) return;
    state.localPage[name] = value; // visible to later templates this run
    ctx.setVar(name, value); // persist into page state (async)
  };
  // Record a leaf failure (so a `try`'s `catch` / an `on_failure` chain can read
  // it as `{{ $page.error }}`) WITHOUT toasting — the caller decides whether the
  // failure is handled (continuation) or hard-stops (legacy toast).
  const markFail = (e: unknown): boolean => {
    state.lastError = errorMessage(e);
    return false;
  };
  // Run an error-recovery sub-chain (`try`'s `catch` / a leaf's `on_failure`)
  // with `{{ $page.error }}` bound to the failure, then clear it. Mirrors the
  // ADR-0022 `try` mechanism; the OUTER chain continues (failure handled).
  const runRecovery = async (chain: unknown): Promise<void> => {
    const prev = state.localPage.error;
    state.localPage.error = state.lastError;
    await runChain(chain, ctx, state, inTry);
    state.localPage.error = prev;
    state.lastError = null;
  };

  // Dispatch ONE leaf action. Returns true on completion, false on a failure
  // (with `state.lastError` set, NOT toasted). Control-flow (`if`/`try`) is
  // handled below, before this runs — so it never reaches the switch.
  const dispatchLeaf = async (): Promise<boolean> => {
    switch (action.type) {
      // -- client ----------------------------------------------------------
      case "set_variable":
        assign(String(action.name ?? ""), arg(action.value, scopeNow()));
        return true;
      case "navigate": {
        const to = arg(action.to, scopeNow());
        if (to) ctx.navigate(String(to));
        return true;
      }
      case "open_drawer": {
        const page = String(arg(action.page, scopeNow()) ?? "").trim();
        // Seed the drawer page's `$page` with `vars` (evaluated NOW against the
        // opener scope) — e.g. open a form drawer for a KNOWN parent
        // (`vars:{modello:"{{ $page.row }}"}`), so the form can prefill + hide it.
        if (page) ctx.openDrawer(page, renderValues(action.vars, scopeNow()));
        return true;
      }
      case "open_overlay": {
        // ADR-0023. Open an authored overlay page; seed its read-only `$input`
        // from the `context` map (evaluated NOW against the opener scope). An
        // `on_close` sub-chain (if any) runs in the OPENER scope when the overlay
        // closes, with the returned `$result` bound — overlaid onto a fresh copy
        // of this same `ctx` (so `setVar`/`refresh` still target the live opener).
        const page = String(arg(action.page, scopeNow()) ?? "").trim();
        if (!page) return true;
        const mode = action.mode === "modal" ? "modal" : "drawer";
        const input = renderValues(action.context, scopeNow());
        const chain = action.on_close;
        const hasChain = Array.isArray(chain) && chain.length > 0;
        ctx.openOverlay({
          page,
          mode,
          input,
          onClose: hasChain
            ? (result) =>
                void runActions(chain, {
                  ...ctx,
                  scope: { ...(ctx.scope as Record<string, unknown>), $result: result } as Scope,
                })
            : undefined,
        });
        return true;
      }
      case "close_overlay": {
        // ADR-0023. Close the enclosing overlay, handing a `result` map (evaluated
        // against the overlay scope) back to the opener's `on_close`. A no-op when
        // not inside an overlay (`closeOverlay` undefined).
        ctx.closeOverlay?.(renderValues(action.result, scopeNow()));
        return true;
      }
      case "open_modal": {
        // ADR-0045. Open an in-page modal/drawer component by node id.
        const target = String(arg(action.target, scopeNow()) ?? "").trim();
        if (target) ctx.openModal(target);
        return true;
      }
      case "close_modal": {
        const target = String(arg(action.target, scopeNow()) ?? "").trim();
        if (target) ctx.closeModal(target);
        return true;
      }
      case "refresh":
        ctx.refresh();
        return true;
      case "toast":
        ctx.toast(String(arg(action.message, scopeNow()) ?? ""));
        return true;

      // -- global store (ADR-0043) -----------------------------------------
      case "global_value": {
        const key = String(action.key ?? "").trim();
        if (key) ctx.storeValue(key, arg(action.value, scopeNow()));
        return true;
      }
      case "global_remove": {
        const key = String(action.key ?? "").trim();
        if (key) ctx.removeValue(key);
        return true;
      }
      case "global_clear":
        ctx.clearStore();
        return true;

      // -- server writes ---------------------------------------------------
      case "create_row": {
        const table = String(action.table ?? "");
        if (!table) return true;
        try {
          await ctx.createRow(table, renderValues(action.values, scopeNow()));
          ctx.refresh();
        } catch (e) {
          return markFail(e);
        }
        return true;
      }
      case "update_row": {
        const table = String(action.table ?? "");
        const rowId = String(arg(action.row, scopeNow()) ?? "");
        if (!table || !rowId) return true;
        try {
          await ctx.updateRow(table, rowId, renderValues(action.values, scopeNow()));
          ctx.refresh();
        } catch (e) {
          return markFail(e);
        }
        return true;
      }
      case "delete_row": {
        const table = String(action.table ?? "");
        const rowId = String(arg(action.row, scopeNow()) ?? "");
        if (!table || !rowId) return true;
        try {
          await ctx.deleteRow(table, rowId);
          ctx.refresh();
        } catch (e) {
          return markFail(e);
        }
        return true;
      }

      // -- integration -----------------------------------------------------
      case "run_workflow": {
        const wf = String(action.workflow ?? "");
        if (!wf) return true;
        try {
          await ctx.runWorkflow(wf, renderValues(action.values, scopeNow()));
        } catch (e) {
          return markFail(e);
        }
        return true;
      }
      case "run_query": {
        // ADR-0043 F3 `.run()` — imperatively (re)run a named page query. It
        // SUBSUMES the former `run_operation` action (ADR-0044 J3c): a write
        // OPERATION is declared as a `trigger:"manual"` operation-query and run
        // here. It AWAITS + PROPAGATES, so a server-side rejection (RLS, state)
        // routes to the chain's `catch` / `on_failure` just like the old action.
        // The op's return lands in `$queries.<name>.data` (no `assign` needed).
        const name = String(action.query ?? "").trim();
        if (!name) return true;
        try {
          await ctx.runQuery(name);
        } catch (e) {
          return markFail(e);
        }
        return true;
      }
      case "print_report": {
        // ADR-0060 S5 — render + download a granted report as PDF. The SERVER
        // re-checks the member's grant (app link ∩ role allow-list), so this
        // can never exceed the role; `params` values are `{{ }}` templates
        // rendered against the live scope (row ids, inputs, …).
        const rep = String(action.report ?? "").trim();
        if (!rep || !ctx.printReport) return true; // no seam → no-op
        try {
          await ctx.printReport(rep, renderValues(action.params, scopeNow()));
        } catch (e) {
          return markFail(e);
        }
        return true;
      }
      case "run_js": {
        // ADR-0044 J2 — call a JS Object ASYNC handler `<Name>.<method>` with
        // the rendered `args` map + the `tray` SDK. The handler is operator-
        // authored client logic; it reaches data only through `tray.*` (RLS
        // re-forced server-side). A failure routes to `on_failure` (F4.1); the
        // return value is optionally bound to a page var via `assign`.
        const ref = String(action.fn ?? "").trim();
        const dot = ref.indexOf(".");
        if (dot <= 0) return true; // not "Name.method" → no-op
        const ns = (scopeNow() as Record<string, unknown>)[ref.slice(0, dot)];
        if (!isJsObjectNamespace(ns)) return true;
        const fn = (ns as Record<string, unknown>)[ref.slice(dot + 1)];
        if (typeof fn !== "function") return true;
        try {
          const res = await (fn as (...a: unknown[]) => unknown).call(
            ns,
            renderValues(action.args, scopeNow()),
            buildTray(ctx, assign),
          );
          if (typeof action.assign === "string" && action.assign) assign(action.assign, res);
        } catch (e) {
          return markFail(e);
        }
        return true;
      }
      case "run_js_inline": {
        // ADR-0044 U-track — the whole handler as inline JS (the "edit as code"
        // toggle). Runs the operator-authored code against the verb SDK; a code
        // error routes to `on_failure` (F4.1) like any leaf failure.
        const code = String(action.code ?? "");
        if (!code.trim()) return true;
        try {
          await runInlineJs(code, ctx, assign, scopeNow());
        } catch (e) {
          return markFail(e);
        }
        return true;
      }

      default:
        return true; // unknown → ignore (forward-compat)
    }
  };

  // Optional per-action confirmation: cancel stops the (sub-)chain.
  if (typeof action.confirm === "string" && action.confirm.trim()) {
    const msg = String(arg(action.confirm, scopeNow()) ?? "");
    if (!ctx.confirm(msg)) return false;
  }

  // -- control flow (ADR-0022): own their own branching, no continuations ---
  if (action.type === "if") {
    const branch = truthy(arg(action.condition, scopeNow())) ? action.then : action.else;
    return runChain(branch, ctx, state, inTry);
  }
  if (action.type === "try") {
    const ok = await runChain(action.do, ctx, state, true);
    if (!ok) await runRecovery(action.catch);
    return true; // a `try` always lets the OUTER chain continue
  }

  // -- action-execution policy in the builder preview (ADR-0045 R3) ---------
  // A SANDBOX (navigation) or GATED (server write / query) leaf is SUPPRESSED
  // in `mode: "preview"`: a toast explains it, the chain CONTINUES. Only LIVE
  // actions run, so the operator tests the UI without leaving the builder or
  // mutating real data. (Control-flow `if`/`try` returned above; their leaves
  // are judged here individually. `run_js*` is LIVE — the sandbox runtime gates
  // its `tray.*` server verbs.)
  if (ctx.mode === "preview") {
    const policy = actionExecPolicy(action.type);
    if (policy !== "live") {
      ctx.toast(
        policy === "gated"
          ? "Azione dati disabilitata in anteprima"
          : "Navigazione disabilitata in anteprima",
      );
      return true;
    }
  }

  // -- leaf action + On-success / On-failure continuations (ADR-0043 F4) ----
  const ok = await dispatchLeaf();
  if (ok) {
    const onSuccess = action.on_success;
    if (Array.isArray(onSuccess) && onSuccess.length) {
      return runChain(onSuccess, ctx, state, inTry); // its own failure propagates
    }
    return true;
  }
  // The leaf FAILED. An `on_failure` chain handles it locally (the outer chain
  // then continues, like a `try`); without one, the legacy stop-and-toast holds.
  const onFailure = action.on_failure;
  if (Array.isArray(onFailure) && onFailure.length) {
    await runRecovery(onFailure);
    return true; // failure handled locally → outer chain continues
  }
  if (!inTry) ctx.toast(state.lastError ?? "Action failed"); // a `try` owns messaging
  return false;
}
